import { isDemoActive } from "./demoFlag";

type RunLoadOptions<TData> = {
  isLoading?: () => boolean;
  setLoading?: (loading: boolean) => void;
  setError?: (message: string | null) => void;
  setHydrated?: (hydrated: boolean) => void;
  hydrateOnError?: boolean;
  fetcher: () => Promise<TData>;
  onSuccess: (data: TData) => void | Promise<void>;
  errorMessage?: string;
};

export async function runLoad<TData>({
  isLoading,
  setLoading,
  setError,
  setHydrated,
  hydrateOnError,
  fetcher,
  onSuccess,
  errorMessage,
}: RunLoadOptions<TData>) {
  if (isLoading?.()) return;
  setLoading?.(true);
  setError?.(null);
  try {
    const data = await fetcher();
    if (isDemoActive()) return;
    await onSuccess(data);
    setHydrated?.(true);
  } catch (error: any) {
    if (isDemoActive()) return;
    if (hydrateOnError) {
      setHydrated?.(true);
    } else {
      setHydrated?.(false);
    }
    setError?.(error?.message || errorMessage || "Failed to load");
  } finally {
    setLoading?.(false);
  }
}

// A list read may overlap a save or a realtime invalidation. Share the request,
// discard superseded snapshots, and perform at most one follow-up per burst.
export function createLoadCoordinator(scope: () => string) {
  let generation = 0;
  let revision = 0;
  let pending: Promise<void> | null = null;
  let queued = false;
  const capture = (includeChanges = false) => {
    const startedGeneration = generation;
    const startedRevision = revision;
    const startedScope = scope();
    return () => generation === startedGeneration && scope() === startedScope
      && (!includeChanges || revision === startedRevision);
  };
  const run = (operation: (current: () => boolean) => Promise<void>, force = false): Promise<void> => {
    if (pending) { queued ||= force; return pending; }
    const contextCurrent = capture();
    const request = (async () => {
      do {
        queued = false;
        await operation(capture(true));
      } while (queued && contextCurrent());
    })().finally(() => { if (pending === request) pending = null; });
    pending = request;
    return request;
  };
  return {
    capture,
    run,
    invalidate: () => { revision += 1; if (pending) queued = true; },
    reset: () => { generation += 1; revision += 1; queued = false; pending = null; },
  };
}
